import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(10, 0),
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(28, 151, 204, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(8),
    position: 'relative',
    zIndex: 1,
  },
  eyebrow: {
    color: '#1c97cc',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: theme.spacing(1),
  },
  title: {
    color: '#ffffff',
    fontWeight: 800,
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    lineHeight: 1.1,
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '1.125rem',
    maxWidth: 600,
    margin: '0 auto',
    lineHeight: 1.6,
  },
  card: {
    background: 'rgba(255,255,255,0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 24,
    padding: theme.spacing(4),
    height: '100%',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    zIndex: 1,
    '&:hover': {
      background: 'rgba(255,255,255,0.06)',
      borderColor: 'rgba(28, 151, 204, 0.3)',
      transform: 'translateY(-8px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(28, 151, 204, 0.1)',
    },
  },
  cardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: 'linear-gradient(90deg, #1c97cc, #3b82f6)',
    borderRadius: '24px 24px 0 0',
  },
  cardIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: 'linear-gradient(135deg, rgba(28, 151, 204, 0.2), rgba(59, 130, 246, 0.1))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    fontSize: '1.5rem',
  },
  cardTitle: {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(0.5),
  },
  cardBrand: {
    color: '#1c97cc',
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  statItem: {
    '&:first-child': {
      borderRight: '1px solid rgba(255,255,255,0.1)',
      paddingRight: theme.spacing(2),
    },
  },
  statLabel: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: theme.spacing(0.5),
  },
  statValue: {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '1.5rem',
  },
  statValueLarge: {
    color: '#1c97cc',
    fontWeight: 800,
    fontSize: '2rem',
  },
  description: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    marginTop: theme.spacing(2),
  },
  tag: {
    display: 'inline-block',
    background: 'rgba(28, 151, 204, 0.15)',
    color: '#1c97cc',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 20,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginTop: theme.spacing(2),
  },
  fullWidthCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  typesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  typeTag: {
    background: 'rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.8)',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 8,
    fontSize: '0.8125rem',
    border: '1px solid rgba(255,255,255,0.1)',
  },
}));

export const CompanyStats = (props) => {
  const classes = useStyles();
  const {
    title = 'Global Trade Excellence',
    subtitle = 'Delivering agricultural commodities and infrastructure solutions across West Africa',
    eyebrow = 'Our Operations',
    showRiceImport = true,
    showRiceReExport = true,
    showFertilizer = true,
    showInfrastructure = true,
    showPetroleum = true,
  } = props;

  return (
    <Box className={classes.root}>
      <Container maxWidth="lg">
        <Box className={classes.header}>
          <Typography className={classes.eyebrow}>{eyebrow}</Typography>
          <Typography variant="h2" className={classes.title}>
            {title}
          </Typography>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </Box>

        <Grid container spacing={3}>
          {showRiceImport && (
            <Grid item xs={12} md={6} lg={4}>
              <Box className={classes.card}>
                <Box className={classes.cardAccent} />
                <Box className={classes.cardIcon}>🌾</Box>
                <Typography className={classes.cardTitle}>Rice Import</Typography>
                <Typography className={classes.cardBrand}>Brand: Halmina</Typography>
                <Box className={classes.statsGrid}>
                  <Box className={classes.statItem}>
                    <Typography className={classes.statLabel}>Volume</Typography>
                    <Typography className={classes.statValue}>10,000 MT</Typography>
                  </Box>
                  <Box>
                    <Typography className={classes.statLabel}>Value</Typography>
                    <Typography className={classes.statValue}>$3.88M</Typography>
                  </Box>
                </Box>
                <span className={classes.tag}>E&P Atlantic Commodity</span>
              </Box>
            </Grid>
          )}

          {showRiceReExport && (
            <Grid item xs={12} md={6} lg={4}>
              <Box className={classes.card}>
                <Box className={classes.cardAccent} />
                <Box className={classes.cardIcon}>🚢</Box>
                <Typography className={classes.cardTitle}>Rice Re-Export</Typography>
                <Typography className={classes.cardBrand}>Premium Quality</Typography>
                <Box className={classes.statsGrid}>
                  <Box className={classes.statItem}>
                    <Typography className={classes.statLabel}>Volume</Typography>
                    <Typography className={classes.statValueLarge}>5,000 MT</Typography>
                  </Box>
                  <Box>
                    <Typography className={classes.statLabel}>Markets</Typography>
                    <Typography className={classes.statValue}>Global</Typography>
                  </Box>
                </Box>
                <Typography className={classes.description}>
                  Connecting regional markets with premium quality rice exports.
                </Typography>
              </Box>
            </Grid>
          )}

          {showFertilizer && (
            <Grid item xs={12} md={6} lg={4}>
              <Box className={classes.card}>
                <Box className={classes.cardAccent} />
                <Box className={classes.cardIcon}>🌱</Box>
                <Typography className={classes.cardTitle}>Fertilizer Distribution</Typography>
                <Typography className={classes.cardBrand}>Agricultural Solutions</Typography>
                <Box className={classes.statsGrid}>
                  <Box className={classes.statItem}>
                    <Typography className={classes.statLabel}>Volume</Typography>
                    <Typography className={classes.statValueLarge}>36K+ MT</Typography>
                  </Box>
                  <Box>
                    <Typography className={classes.statLabel}>Value</Typography>
                    <Typography className={classes.statValue}>$27.7M</Typography>
                  </Box>
                </Box>
                <Box className={classes.typesContainer}>
                  <span className={classes.typeTag}>Urea</span>
                  <span className={classes.typeTag}>NPK +4MgO</span>
                  <span className={classes.typeTag}>NPK 6:20:10</span>
                </Box>
              </Box>
            </Grid>
          )}

          {showInfrastructure && (
            <Grid item xs={12} md={6}>
              <Box className={`${classes.card} ${classes.fullWidthCard}`}>
                <Box className={classes.cardAccent} />
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Box className={classes.cardIcon}>💧</Box>
                    <Typography className={classes.cardTitle}>Infrastructure Solutions</Typography>
                    <Typography className={classes.cardBrand}>Water Meters</Typography>
                    <Typography className={classes.description}>
                      Delivering essential infrastructure equipment for utility management across The Gambia.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box className={classes.statsGrid}>
                      <Box className={classes.statItem}>
                        <Typography className={classes.statLabel}>Units</Typography>
                        <Typography className={classes.statValueLarge}>4,000</Typography>
                      </Box>
                      <Box>
                        <Typography className={classes.statLabel}>Value</Typography>
                        <Typography className={classes.statValue}>$354K</Typography>
                      </Box>
                    </Box>
                    <span className={classes.tag}>E&P Atlantic Infrastructure</span>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}

          {showPetroleum && (
            <Grid item xs={12} md={6}>
              <Box className={`${classes.card} ${classes.fullWidthCard}`}>
                <Box className={classes.cardAccent} />
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Box className={classes.cardIcon}>⛽</Box>
                    <Typography className={classes.cardTitle}>Petroleum & Energy</Typography>
                    <Typography className={classes.cardBrand}>Business Facilitation</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography className={classes.description}>
                      Strategic business facilitation services for international petroleum companies 
                      seeking oil block acquisitions in The Gambia.
                    </Typography>
                    <span className={classes.tag}>Energy Sector</span>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};
